import { Box } from '@mui/material';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import ApplicantForm from '../components/applicantForm';
import OrganizationForm from '../components/organizationForm';
import { Modal, ModalHeader } from 'components/common/Modal';

import api from 'api';
import { Permission } from 'api/models/response/permissions';
import PERMISSION from 'assets/permissions.json';
import { FEATURE } from 'constant/features';
import { useHasFeature } from 'hooks/useHasFeature';
import useTranslate from 'hooks/useTranslate';
import { RootState } from 'redux/store';
import { OptionInterface, tabValues } from 'types';

interface Props {
    open: boolean;
    onClose: () => void;
    appId: string;
}

const OnboardingModalV2 = ({ open, onClose, appId }: Props) => {
    const { translate } = useTranslate();
    const currentUserId = useSelector((state: RootState) => state.user.entity.id);
    const hasPartnerOrganizations = useHasFeature(FEATURE.PARTNER_ORGANIZATIONS);
    const [selectedTab, setSelectedTab] = useState<tabValues>('applicant');
    const [companyOptions, setCompanyOptions] = useState<OptionInterface[]>([]);
    const [canOnboardOrganizations, setCanOnboardOrganizations] = useState(false);
    const isApplicantView = selectedTab === 'applicant';
    const title = isApplicantView ? translate('applicant') : translate('organization');
    const subtitle = isApplicantView
        ? 'Add an applicant, confirm their contact details, and choose which onboarding actions they can access next.'
        : 'Add an organization, confirm the primary contact details, and choose how onboarding should continue.';

    useEffect(() => {
        const loadCompanies = async () => {
            try {
                const response = await api.clientServiceProviders.getClientServiceProviders();
                const data = response.data.map((obj) => ({
                    label: obj.company_name,
                    value: obj.id,
                }));
                setCompanyOptions(data as OptionInterface[]);
            } catch {
                setCompanyOptions([]);
            }
        };

        loadCompanies();
    }, []);

    useEffect(() => {
        if (!open || !currentUserId) {
            setCanOnboardOrganizations(false);
            setSelectedTab('applicant');
            return;
        }

        const loadPermissions = async () => {
            try {
                const response = await api.permissions.getPermissions(currentUserId);
                const hasOnboardOrganizationsPermission = response?.data.some(
                    (permission: Permission) => permission.name === PERMISSION.ONBOARD_ORGANIZATIONS
                );

                setCanOnboardOrganizations(
                    hasPartnerOrganizations && Boolean(hasOnboardOrganizationsPermission)
                );

                if (!hasPartnerOrganizations || !hasOnboardOrganizationsPermission) {
                    setSelectedTab('applicant');
                }
            } catch {
                setCanOnboardOrganizations(false);
                setSelectedTab('applicant');
            }
        };

        loadPermissions();
    }, [open, currentUserId, hasPartnerOrganizations]);

    if (!open) return null;

    return (
        <Modal open={open} onClose={onClose} size="md" ariaLabel="Onboarding">
            <ModalHeader eyebrow={translate('onboarding')} title={title} subtitle={subtitle} />

            {selectedTab === 'applicant' ? (
                <ApplicantForm
                    companyOptions={companyOptions}
                    onClose={onClose}
                    topContent={
                        canOnboardOrganizations ? (
                            <ApplicationOptions value={selectedTab} onChange={setSelectedTab} />
                        ) : undefined
                    }
                />
            ) : (
                <OrganizationForm
                    onClose={onClose}
                    topContent={
                        canOnboardOrganizations ? (
                            <ApplicationOptions value={selectedTab} onChange={setSelectedTab} />
                        ) : undefined
                    }
                />
            )}
        </Modal>
    );
};

export default OnboardingModalV2;

const ApplicationOptions = ({
    value,
    onChange,
}: {
    value: tabValues;
    onChange: (value: tabValues) => void;
}) => {
    const { translate } = useTranslate();
    const options = [
        { label: translate('applicant'), value: 'applicant' },
        { label: translate('organization'), value: 'organization' },
    ];

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                gap: 1,
                p: 0.75,
                border: '1px solid rgba(148, 163, 184, 0.18)',
                borderRadius: '16px',
                background: 'rgba(255, 255, 255, 0.72)',
                backdropFilter: 'blur(12px)',
            }}
        >
            {options.map((opt) => {
                const active = value === opt.value;

                return (
                    <Box
                        key={opt.value}
                        component="button"
                        type="button"
                        onClick={() => onChange(opt.value as tabValues)}
                        sx={{
                            height: 42,
                            px: 2,
                            border: 'none',
                            borderRadius: '12px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: 600,
                            color: active ? '#ffffff' : '#475569',
                            background: active
                                ? 'linear-gradient(135deg, #8353e2 0%, #6d41d8 100%)'
                                : 'transparent',
                            boxShadow: active ? '0 12px 24px rgba(131, 83, 226, 0.22)' : 'none',
                            transition:
                                'background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
                            '&:hover': {
                                transform: 'translateY(-1px)',
                                background: active
                                    ? 'linear-gradient(135deg, #8353e2 0%, #6d41d8 100%)'
                                    : 'rgba(148, 163, 184, 0.08)',
                            },
                        }}
                    >
                        {opt.label}
                    </Box>
                );
            })}
        </Box>
    );
};
